import { FC } from 'react';
import { DatePicker, Input, InputNumber, Space } from 'antd';

import { PlatformDatePicker } from '@/shared';
import { AlertCondition, AlertFieldDefinition } from '@/types';
import { dayjs } from '@/utilities';

interface AlertConditionValueInputProps {
    condition: AlertCondition;
    fieldType: AlertFieldDefinition['type'];
    onChange: (value: AlertCondition['value']) => void;
}

const asNumberRange = (value: AlertCondition['value']): [number, number] => {
    const values = Array.isArray(value) ? value : [];
    return [typeof values[0] === 'number' ? values[0] : 0, typeof values[1] === 'number' ? values[1] : 0];
};

const asDateRange = (value: AlertCondition['value']) => {
    const values = Array.isArray(value) ? value : [];
    const start = typeof values[0] === 'string' ? dayjs(values[0]) : null;
    const end = typeof values[1] === 'string' ? dayjs(values[1]) : null;

    return [start?.isValid() ? start : null, end?.isValid() ? end : null] as const;
};

const AlertConditionValueInput: FC<AlertConditionValueInputProps> = ({
    condition,
    fieldType,
    onChange,
}): JSX.Element => {
    if (condition.operator === 'changed') {
        return <Input disabled size='small' placeholder='Any Change...' value='' />;
    }

    if (condition.operator === 'between' && fieldType === 'number') {
        const range = asNumberRange(condition.value);
        return (
            <Space.Compact block>
                <InputNumber
                    aria-label='Range minimum'
                    size='small'
                    value={range[0]}
                    onChange={(value) => onChange([value ?? 0, range[1]])}
                />
                <InputNumber
                    aria-label='Range maximum'
                    size='small'
                    value={range[1]}
                    onChange={(value) => onChange([range[0], value ?? 0])}
                />
            </Space.Compact>
        );
    }

    if (condition.operator === 'between' && fieldType === 'date') {
        return (
            <DatePicker.RangePicker
                allowEmpty={[false, false]}
                aria-label='Date range'
                size='small'
                value={[...asDateRange(condition.value)]}
                onChange={(value) =>
                    value?.[0] && value[1]
                        ? onChange([value[0].format('YYYY-MM-DD'), value[1].format('YYYY-MM-DD')])
                        : undefined
                }
            />
        );
    }

    if (fieldType === 'number' || condition.operator === 'withinDays') {
        return (
            <InputNumber
                min={condition.operator === 'withinDays' ? 0 : undefined}
                size='small'
                style={{ minWidth: 0, width: '100%' }}
                value={typeof condition.value === 'number' ? condition.value : undefined}
                onChange={(value) => onChange(value ?? 0)}
            />
        );
    }

    if (fieldType === 'date') {
        const value = typeof condition.value === 'string' ? dayjs(condition.value) : null;
        return (
            <PlatformDatePicker
                size='small'
                style={{ minWidth: 0, width: '100%' }}
                value={value?.isValid() ? value : null}
                onChange={(nextValue) => onChange(nextValue?.format('YYYY-MM-DD') ?? '')}
            />
        );
    }

    return (
        <Input
            size='small'
            style={{ minWidth: 0, width: '100%' }}
            value={Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value)}
            placeholder='Value...'
            onChange={(event) => onChange(event.target.value)}
        />
    );
};

export default AlertConditionValueInput;
